import { useEffect } from 'react'

/**
 * Elements the torch lights.
 *
 * Cards and buttons — the things with an edge to catch the light. A
 * paragraph has no material to illuminate, and lighting everything would
 * make the effect mean nothing.
 */
const LIT = '[data-torch], .torch'

/**
 * Makes the cursor a soft light source over cards and buttons.
 *
 * Writes the pointer's position within the hovered element as `--torch-x`
 * and `--torch-y` (percentages); `effects.css` paints a radial highlight
 * there, so the near edges and corners brighten as the cursor approaches
 * them.
 *
 * ## One listener, not one per element
 *
 * A `pointermove` handler on every card would mean dozens of listeners and
 * dozens of React subscriptions on a page that has dozens of cards. One
 * delegated listener on the document costs the same whatever the page holds.
 *
 * ## Why it writes CSS variables rather than React state
 *
 * Pointer moves fire at the display's refresh rate. Routing that through
 * React would re-render the tree ~120 times a second for a lighting effect.
 * A custom property change is handled by the style system alone, and the
 * write is batched into an animation frame so several moves in one frame
 * cost one update.
 *
 * ## Pointer only
 *
 * Gated on `(pointer: fine)`. A finger has no hover state, so on a phone
 * this would light elements at whatever position the last tap happened to
 * be — and the listener would be pure cost for an effect nobody sees.
 */
export const useTorch = (): void => {
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!window.matchMedia('(pointer: fine)').matches) return

    let frame = 0
    let pending: { el: HTMLElement; x: number; y: number } | null = null
    let lit: HTMLElement | null = null

    const flush = () => {
      frame = 0
      if (!pending) return
      const { el, x, y } = pending
      el.style.setProperty('--torch-x', `${x}%`)
      el.style.setProperty('--torch-y', `${y}%`)
      pending = null
    }

    const clear = (el: HTMLElement) => {
      el.style.removeProperty('--torch-x')
      el.style.removeProperty('--torch-y')
    }

    const onMove = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse') return

      const node = event.target
      const el =
        node instanceof Element ? node.closest<HTMLElement>(LIT) : null

      if (el !== lit) {
        // Leave the previous one dark rather than frozen mid-glow.
        if (lit) clear(lit)
        lit = el
      }
      if (!el) return

      const rect = el.getBoundingClientRect()
      if (!rect.width || !rect.height) return

      pending = {
        el,
        x: ((event.clientX - rect.left) / rect.width) * 100,
        y: ((event.clientY - rect.top) / rect.height) * 100,
      }
      frame ||= requestAnimationFrame(flush)
    }

    document.addEventListener('pointermove', onMove, { passive: true })

    return () => {
      document.removeEventListener('pointermove', onMove)
      if (frame) cancelAnimationFrame(frame)
      if (lit) clear(lit)
    }
  }, [])
}
